<template>
  <div class="model-config">
    <div class="panel-title-row">
      <h2>Model Configuration</h2>
      <AdminHelpButton
        floating-width="680px"
        presentation="floating"
        :topic="MODEL_CONFIGURATION_HELP"
      />
    </div>
    <p class="hint">
      Admin only. Pick a group and role, then <strong>Load</strong> any version
      into the editor, <strong>Activate</strong> an existing version (including
      rolling back to a previous one), or <strong>Save as new version</strong>
      to upload edited prompts. Versions are never overwritten or deleted.
    </p>

    <!-- Selection: group / role / family -->
    <div class="row">
      <div class="field">
        <label>Group</label>
        <select v-model="groupId" @change="onSelectionChange">
          <option :value="null" disabled>Select a group…</option>
          <option v-for="g in groups" :key="g.id" :value="g.id">
            {{ g.name }}
          </option>
        </select>
      </div>

      <div class="field">
        <label>Model role</label>
        <select v-model="modelGroup" @change="onSelectionChange">
          <option v-for="mg in MODEL_GROUPS" :key="mg" :value="mg">
            {{ mg }}
          </option>
        </select>
      </div>

      <div class="field">
        <label>Family</label>
        <select v-model="family">
          <option v-for="f in familyOptions" :key="f" :value="f">{{ f }}</option>
        </select>
      </div>
    </div>

    <!-- Version picker + version-scoped actions -->
    <div class="row version-row">
      <div class="field grow">
        <label>Version</label>
        <select v-model="selectedVersionName" :disabled="busy || !versions.length">
          <option v-if="!versions.length" :value="''">No versions yet</option>
          <option v-for="v in versions" :key="v.name" :value="v.name">
            {{ versionLabel(v) }}
          </option>
        </select>
        <p class="loaded-version" :class="{ warning: selectedDiffersFromLoaded }">
          <template v-if="loadedModel">
            Loaded in editor: <code>{{ loadedModel.name }}</code>
            <span v-if="selectedDiffersFromLoaded">
              Select Load before editing <code>{{ selectedVersionName }}</code>.
            </span>
          </template>
          <template v-else>
            No version loaded yet. Select a version, then click Load.
          </template>
        </p>
      </div>
      <div class="field version-actions">
        <button
          class="btn secondary tooltip-btn"
          data-tooltip="Dropdown changes only the target; Load changes the editor."
          :disabled="busy || !selectedVersionName"
          title="Load the selected version into the prompt editor before editing."
          @click="loadSelected"
        >
          Load into editor
        </button>
        <button
          class="btn secondary"
          :disabled="busy || !selectedVersionName || selectedIsActive"
          title="Activate the selected version without editing or saving a new copy."
          @click="activateSelected"
        >
          Activate selected
        </button>
      </div>
    </div>

    <!-- Metadata -->
    <div class="row">
      <div class="field">
        <label>Owner</label>
        <input type="text" v-model="owner" placeholder="Admin" />
      </div>
      <div class="field checkbox">
        <label><input type="checkbox" v-model="apiBased" /> API-based</label>
      </div>
    </div>

    <div class="field">
      <label>Description</label>
      <input type="text" v-model="description" placeholder="Short description" />
    </div>

    <!-- Prompts JSON -->
    <div class="field">
      <div class="label-row">
        <label>Prompts (JSON)</label>
        <button type="button" class="link-btn" @click="format('prompts')">Format</button>
      </div>
      <textarea
        v-model="promptsText"
        rows="14"
        spellcheck="false"
        class="code"
        :class="{ invalid: promptsLint.error }"
        placeholder='{ "first_session": { "role": "system", "content": "…use \n for line breaks…" } }'
      ></textarea>
      <p v-if="promptsLint.error" class="lint-error">⚠ {{ promptsLint.error }}</p>
      <p v-else class="lint-ok">✓ Valid JSON</p>
    </div>

    <!-- Parameters JSON -->
    <div class="field">
      <div class="label-row">
        <label>Parameters (JSON)</label>
        <button type="button" class="link-btn" @click="format('parameters')">Format</button>
      </div>
      <textarea
        v-model="parametersText"
        rows="5"
        spellcheck="false"
        class="code"
        :class="{ invalid: parametersLint.error }"
        placeholder='{ "model": "qwen3.6:35b-mlx", "temperature": 0.7 }'
      ></textarea>
      <p v-if="parametersLint.error" class="lint-error">⚠ {{ parametersLint.error }}</p>
      <p v-else class="lint-ok">✓ Valid JSON</p>
    </div>

    <!-- Review changes against the version that was loaded into the editor -->
    <div class="review" v-if="loadedModel">
      <div class="review-head">
        <button type="button" class="link-btn" @click="reviewOpen = !reviewOpen">
          {{ reviewOpen ? "Hide" : "Show" }} changes vs {{ loadedModel.name }}
        </button>
        <span class="review-stats" v-if="pendingDiff">
          <template v-if="pendingDiff.identical">no changes</template>
          <template v-else>
            <span class="added">+{{ pendingDiff.stats.added }}</span>
            <span class="removed">−{{ pendingDiff.stats.removed }}</span>
          </template>
        </span>
        <span class="review-stats invalid" v-else>fix the JSON errors above to see the diff</span>
      </div>
      <ModelDiffView
        v-if="reviewOpen && pendingDiff"
        :left-text="baselineText"
        :right-text="draftText"
        :left-label="loadedModel.name"
        right-label="editor"
        mode="unified"
        empty-message="The editor matches the loaded version."
      />
    </div>

    <!-- Save as new version -->
    <div class="button-row">
      <span class="next-version" v-if="groupId !== null">
        Will create <code>{{ nextVersionName }}</code>
      </span>
      <label class="activate-toggle">
        <input type="checkbox" v-model="activateAfterSave" /> Activate after save
      </label>
      <button class="btn primary" :disabled="!canSave || busy" @click="saveAsNewVersion">
        Save as new version
      </button>
    </div>
  </div>
</template>

<script>
import modelApi from "../services/model_api";
import manageApi from "../services/manage_api";
import swal from "../services/swal";
import { FALLBACK_FAMILIES, FALLBACK_MODEL_GROUPS, versionNum, lintJson } from "../utils/models";
import { toCanonicalText, buildHunks } from "../utils/model_diff";
import { MODEL_CONFIGURATION_HELP } from "../utils/admin_help";
import AdminHelpButton from "./AdminHelpButton.vue";
import ModelDiffView from "./ModelDiffView.vue";

export default {
  name: "ModelConfigPanel",
  components: { AdminHelpButton, ModelDiffView },
  data() {
    return {
      MODEL_CONFIGURATION_HELP,
      // Replaced by GET /model/options on mount; see modelApi.loadOptions.
      FAMILIES: FALLBACK_FAMILIES,
      MODEL_GROUPS: FALLBACK_MODEL_GROUPS,
      groups: [],
      // selection
      groupId: null,
      modelGroup: "core",
      family: "ollama-qwen",
      // versions for the current group+role (full model objects)
      versions: [],
      selectedVersionName: "",
      // editor
      owner: "Admin",
      description: "",
      apiBased: false,
      promptsText: "",
      parametersText: "",
      activateAfterSave: true,
      busy: false,
      // Version currently loaded into the editor, kept as the diff baseline.
      loadedModel: null,
      reviewOpen: false,
    };
  },
  computed: {
    groupName() {
      const g = this.groups.find((x) => x.id === this.groupId);
      return g ? g.name : "";
    },
    // A stored version may carry a family the backend no longer serves. Keep it
    // in the list so loading that version does not silently blank the select and
    // then save a different family than the one it had.
    familyOptions() {
      if (this.family && !this.FAMILIES.includes(this.family)) {
        return [...this.FAMILIES, this.family];
      }
      return this.FAMILIES;
    },
    activeVersion() {
      return this.versions.find((v) => v.activated) || null;
    },
    selectedIsActive() {
      return !!this.activeVersion && this.activeVersion.name === this.selectedVersionName;
    },
    selectedDiffersFromLoaded() {
      return !!(
        this.loadedModel &&
        this.selectedVersionName &&
        this.loadedModel.name !== this.selectedVersionName
      );
    },
    nextVersionNumber() {
      const max = this.versions.reduce((acc, v) => Math.max(acc, versionNum(v.name)), 0);
      return max + 1;
    },
    nextVersionName() {
      if (!this.groupName || !this.modelGroup) return "—";
      return this.buildName(this.nextVersionNumber);
    },
    promptsLint() {
      return lintJson(this.promptsText);
    },
    parametersLint() {
      return lintJson(this.parametersText);
    },
    canSave() {
      return (
        this.groupId !== null &&
        !!this.modelGroup &&
        !!this.family &&
        !this.promptsLint.error &&
        !this.parametersLint.error
      );
    },
    // The editor buffer shaped like a model row, so it can be fed to the same
    // canonical renderer the compare panel uses. Null while the JSON is invalid.
    draftModel() {
      if (this.promptsLint.error || this.parametersLint.error) return null;
      return {
        family: this.family,
        api_based: this.apiBased,
        owner: this.owner || "Admin",
        description: this.description || "",
        prompts: this.promptsLint.value,
        parameters: this.parametersLint.value,
      };
    },
    baselineText() {
      return this.loadedModel ? toCanonicalText(this.loadedModel) : "";
    },
    draftText() {
      return this.draftModel ? toCanonicalText(this.draftModel) : "";
    },
    pendingDiff() {
      if (!this.loadedModel || !this.draftModel) return null;
      return buildHunks(this.baselineText, this.draftText);
    },
    hasPendingChanges() {
      return !!this.pendingDiff && !this.pendingDiff.identical;
    },
    // Suffix for the save confirmation so the count is visible even if the
    // admin never expands the review section.
    pendingChangeSummary() {
      if (!this.pendingDiff || this.pendingDiff.identical) return "";
      const { added, removed } = this.pendingDiff.stats;
      return `\n\nChanges vs ${this.loadedModel.name}: +${added} / −${removed} lines.`;
    },
  },
  watch: {
    // Open the review section the first time the buffer diverges from the
    // loaded version, so a save is never a blind one.
    hasPendingChanges(now) {
      if (now) this.reviewOpen = true;
    },
  },
  async mounted() {
    const options = await modelApi.loadOptions();
    this.FAMILIES = options.families;
    this.MODEL_GROUPS = options.modelGroups;
    try {
      const groups = await manageApi.getGroups(true);
      this.groups = Array.isArray(groups) ? groups : [];
    } catch (e) {
      swal.fire({ icon: "error", title: "Could not load groups", text: String(e) });
    }
  },
  methods: {
    pad3(n) {
      return String(n).padStart(3, "0");
    },
    buildName(version) {
      return `${this.modelGroup}_${this.groupName}_version_${this.pad3(version)}`;
    },
    versionLabel(v) {
      return v.activated ? `${v.name} (active)` : v.name;
    },
    async onSelectionChange() {
      this.selectedVersionName = "";
      this.versions = [];
      // A baseline from another group or role would make the diff meaningless.
      this.loadedModel = null;
      this.reviewOpen = false;
      if (this.groupId !== null && this.modelGroup) await this.refreshVersions();
    },
    // Load all versions for the current group+role; newest first. Default the
    // dropdown to the active version (else the newest).
    async refreshVersions() {
      this.busy = true;
      try {
        const models = await modelApi.listModels({
          group_id: this.groupId,
          model_group: this.modelGroup,
        });
        this.versions = (Array.isArray(models) ? models : []).sort(
          (a, b) => versionNum(b.name) - versionNum(a.name)
        );
        const active = this.versions.find((v) => v.activated);
        this.selectedVersionName = active
          ? active.name
          : this.versions[0]?.name || "";
      } catch (e) {
        swal.fire({ icon: "error", title: "Could not load versions", text: String(e) });
      } finally {
        this.busy = false;
      }
    },
    format(which) {
      const key = which === "prompts" ? "promptsText" : "parametersText";
      const lint = lintJson(this[key]);
      if (lint.error) {
        swal.fire({ icon: "warning", title: "Cannot format", text: lint.error });
        return;
      }
      this[key] = JSON.stringify(lint.value, null, 2);
    },
    // Load the version selected in the dropdown into the editor.
    loadSelected() {
      const v = this.versions.find((x) => x.name === this.selectedVersionName);
      if (!v) return;
      this.family = v.family ?? this.family;
      this.owner = v.owner ?? this.owner;
      this.description = v.description ?? "";
      this.apiBased = !!v.api_based;
      this.promptsText = JSON.stringify(v.prompts ?? {}, null, 2);
      this.parametersText = JSON.stringify(v.parameters ?? {}, null, 2);
      // Snapshot as the diff baseline. Cloned so later edits to `versions`
      // cannot mutate what we are comparing against.
      this.loadedModel = JSON.parse(JSON.stringify(v));
      this.reviewOpen = false;
    },
    // Activate the selected existing version (no upload). Enables rollback.
    async activateSelected() {
      if (!this.selectedVersionName || this.selectedIsActive) return;
      const confirm = await swal.fire({
        icon: "warning",
        title: `Activate ${this.selectedVersionName}?`,
        text:
          "This deactivates the current version and activates the selected one. " +
          "Active chat connections in this group will be closed so they reconnect " +
          "on the newly activated model.",
        showCancelButton: true,
        confirmButtonText: "Activate",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;

      this.busy = true;
      try {
        const res = await modelApi.activateModel({
          group_id: this.groupId,
          name: this.selectedVersionName,
          model_group: this.modelGroup,
        });
        await this.refreshVersions();
        swal.fire({ icon: "success", title: "Activated", text: res?.message ?? "" });
      } catch (e) {
        swal.fire({ icon: "error", title: "Activate failed", text: String(e) });
      } finally {
        this.busy = false;
      }
    },
    buildPayload(version) {
      return {
        group_id: this.groupId,
        name: this.buildName(version),
        model_group: this.modelGroup,
        family: this.family,
        owner: this.owner || "Admin",
        description: this.description || "",
        api_based: this.apiBased,
        prompts: this.promptsLint.value,
        parameters: this.parametersLint.value,
        activated: false, // backend forces this false anyway
      };
    },
    // Upload, retrying with a bumped number if the name already exists
    // (e.g. someone created a version between our list and our save).
    async uploadWithRetry(startVersion) {
      let v = startVersion;
      for (let attempt = 0; attempt < 6; attempt++) {
        const res = await modelApi.uploadModel(this.buildPayload(v));
        const msg = res?.message ?? "";
        if (/already exists/i.test(msg)) {
          v += 1;
          continue;
        }
        return { version: v, message: msg };
      }
      throw new Error("Could not find an unused version after several attempts.");
    },
    // Save the edited JSON as the next version, optionally activating it.
    async saveAsNewVersion() {
      if (!this.canSave) return;
      const confirm = await swal.fire({
        icon: "question",
        title: `Create ${this.nextVersionName}?`,
        text:
          (this.activateAfterSave
            ? "This uploads the edited prompts as a new version and activates it. " +
              "Active chat connections in this group will be closed so they reconnect " +
              "on the new model."
            : "This uploads the edited prompts as a new version (not activated).") +
          this.pendingChangeSummary,
        showCancelButton: true,
        confirmButtonText: "Create",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;

      this.busy = true;
      try {
        const { version, message } = await this.uploadWithRetry(this.nextVersionNumber);
        let text = message;
        if (this.activateAfterSave) {
          const act = await modelApi.activateModel({
            group_id: this.groupId,
            name: this.buildName(version),
            model_group: this.modelGroup,
          });
          text += `\n\n${act?.message ?? ""}`;
        }
        await this.refreshVersions();
        this.selectedVersionName = this.buildName(version);
        // The version we just wrote becomes the new baseline, so the review
        // section reads "no changes" instead of re-showing the edit we saved.
        const created = this.versions.find((v) => v.name === this.selectedVersionName);
        if (created) this.loadedModel = JSON.parse(JSON.stringify(created));
        swal.fire({ icon: "success", title: "Saved", text });
      } catch (e) {
        swal.fire({ icon: "error", title: "Save failed", text: String(e) });
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.model-config {
  padding: 20px;
  background-color: #ffffffb2;
  /* Top-left is square so the panel joins the tab strip above it. */
  border-radius: 0 20px 20px 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  /* Side inset comes from .config-shell so both tabs are exactly as wide. */
  margin: 0 0 20px;
  max-width: none;
  width: 100%;
  text-align: left;
}
.hint {
  font-size: 0.9em;
  color: #444;
  margin-bottom: 16px;
}
.panel-title-row {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 6px;
}
.panel-title-row h2 {
  margin: 0;
}
.row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.version-row {
  align-items: flex-start;
}
.field {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  flex: 1;
  min-width: 180px;
}
.field.grow {
  flex: 2;
}
.field.checkbox {
  justify-content: center;
}
.field label {
  margin-bottom: 6px;
  font-weight: 600;
}
.version-actions {
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: flex-start;
  padding-top: 27px;
}
.loaded-version {
  color: #555;
  font-size: 0.82em;
  line-height: 1.4;
  margin: 6px 0 0;
}
.loaded-version.warning {
  color: #8a5b00;
  font-weight: 600;
}
.loaded-version code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.field input,
.field select,
.field textarea {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
textarea.code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85em;
  /* VS Code-style soft word wrap: wrap long lines instead of scrolling. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
textarea.invalid {
  border-color: #d33;
  background-color: #fff5f5;
}
.lint-error {
  color: #d33;
  font-size: 0.82em;
  margin: 6px 0 0;
}
.lint-ok {
  color: #2a8a3e;
  font-size: 0.82em;
  margin: 6px 0 0;
}
.actions-inline {
  display: flex;
  gap: 12px;
}
.link-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0;
  font-size: 0.85em;
  text-decoration: underline;
}
.review {
  margin-bottom: 16px;
}
.review-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.review-stats {
  font-size: 0.82em;
  color: #555;
  display: flex;
  gap: 8px;
  font-weight: 600;
}
.review-stats.invalid {
  color: #d33;
  font-weight: 400;
}
.review-stats .added {
  color: #1a7f37;
}
.review-stats .removed {
  color: #a4262c;
}
.next-version {
  font-size: 0.85em;
  color: #444;
  margin-right: auto;
}
.next-version code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.button-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}
.activate-toggle {
  font-size: 0.9em;
}
.btn {
  padding: 10px 20px;
  font-size: 16px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.btn.primary {
  background-color: var(--primary-color);
}
.btn.secondary {
  background-color: #6c757d;
}
.tooltip-btn {
  position: relative;
}
.tooltip-btn::after {
  background: #24313f;
  border-radius: 4px;
  bottom: calc(100% + 8px);
  color: #fff;
  content: attr(data-tooltip);
  font-size: 12px;
  font-weight: 500;
  left: 50%;
  line-height: 1.35;
  opacity: 0;
  padding: 7px 9px;
  pointer-events: none;
  position: absolute;
  transform: translateX(-50%);
  transition: opacity 0.12s ease;
  visibility: hidden;
  white-space: normal;
  width: 240px;
  z-index: 10;
}
.tooltip-btn:hover::after,
.tooltip-btn:focus::after {
  opacity: 1;
  visibility: visible;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
